import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils";

const badgeVariants = cva(
	"inline-flex items-center justify-center min-w-0 border border-neutral-200 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-neutral-950 focus:ring-offset-2 dark:border-neutral-800 dark:focus:ring-neutral-300  disabled:!opacity-70",
	{
		variants: {
			// One name per look: two variants must never share a class string, and
			// `badge.test.tsx` fails if they do. Use `neutral`, `success`, `danger`, `info`,
			// `purple` and `yellow`; the aliases `grey`, `muted`, `green`, `red`,
			// `blue`, `violet` and `gold` were removed in APP-28776.
			//
			// Some pairs look alike but are intentionally different, so do not merge them:
			// - `warning`, `orange` and `yellow` are three different looks.
			// - `destructive` is solid red; `danger` is pale red.
			// - `black` has darker text than `neutral` on the same background.
			// - `default` is near-black; `neutral` is light.
			variant: {
				default:
					"border-transparent bg-neutral-900 text-neutral-50 hover:bg-neutral-900/80 dark:bg-neutral-50 dark:text-neutral-900 dark:hover:bg-neutral-50/80 disabled:!bg-neutral-900 dark:disabled:!bg-neutral-50",
				secondary:
					"border-transparent bg-neutral-100 text-neutral-900 hover:bg-neutral-100/80 dark:bg-neutral-800 dark:text-neutral-50 dark:hover:bg-neutral-800/80 disabled:!bg-neutral-100 dark:disabled:!bg-neutral-800",
				destructive:
					"border-transparent bg-red-500 text-neutral-50 hover:bg-red-500/80 dark:bg-red-600 dark:text-neutral-50 dark:hover:bg-red-600/80 disabled:!bg-red-500 dark:disabled:!bg-red-600",
				outline:
					"text-neutral-950 dark:text-neutral-50 disabled:!bg-transparent",
				neutral:
					"border-badge-border bg-badge-neutral text-badge-neutral-foreground disabled:!bg-badge-neutral",
				success:
					"border-badge-border bg-badge-success text-badge-success-foreground disabled:!bg-badge-success",
				danger:
					"border-badge-border bg-badge-danger text-badge-danger-foreground disabled:!bg-badge-danger",
				info: "border-badge-border bg-badge-info text-badge-info-foreground disabled:!bg-badge-info",
				purple:
					"border-badge-border bg-badge-purple text-badge-purple-foreground disabled:!bg-badge-purple",
				teal: "border-badge-border bg-badge-teal text-badge-teal-foreground disabled:!bg-badge-teal",
				yellow:
					"border-badge-border bg-badge-yellow text-badge-yellow-foreground disabled:!bg-badge-yellow",
				warning:
					"border-badge-border bg-badge-warning text-badge-warning-foreground disabled:!bg-badge-warning",
				orange:
					"border-badge-border bg-badge-orange text-badge-orange-foreground disabled:!bg-badge-orange",
				indigo:
					"border-badge-border bg-badge-indigo text-badge-indigo-foreground disabled:!bg-badge-indigo",
				magenta:
					"border-badge-border bg-badge-magenta text-badge-magenta-foreground disabled:!bg-badge-magenta",
				cyan: "border-badge-border bg-badge-cyan text-badge-cyan-foreground disabled:!bg-badge-cyan",
				amber:
					"border-badge-border bg-badge-amber text-badge-amber-foreground disabled:!bg-badge-amber",
				seafoam:
					"border-badge-border bg-badge-seafoam text-badge-seafoam-foreground disabled:!bg-badge-seafoam",
				fuchsia:
					"border-badge-border bg-badge-fuchsia text-badge-fuchsia-foreground disabled:!bg-badge-fuchsia",
				coral:
					"border-badge-border bg-badge-coral text-badge-coral-foreground disabled:!bg-badge-coral",
				navy: "border-badge-border bg-badge-navy text-badge-navy-foreground disabled:!bg-badge-navy",
				lavender:
					"border-badge-border bg-badge-lavender text-badge-lavender-foreground disabled:!bg-badge-lavender",
				bronze:
					"border-badge-border bg-badge-bronze text-badge-bronze-foreground disabled:!bg-badge-bronze",
				aqua: "border-badge-border bg-badge-aqua text-badge-aqua-foreground disabled:!bg-badge-aqua",
				periwinkle:
					"border-badge-border bg-badge-periwinkle text-badge-periwinkle-foreground disabled:!bg-badge-periwinkle",
				plum: "border-badge-border bg-badge-plum text-badge-plum-foreground disabled:!bg-badge-plum",
				peach:
					"border-badge-border bg-badge-peach text-badge-peach-foreground disabled:!bg-badge-peach",
				copper:
					"border-badge-border bg-badge-copper text-badge-copper-foreground disabled:!bg-badge-copper",
				black:
					"border-badge-border bg-badge-black text-badge-black-foreground disabled:!bg-badge-black",
				lime: "border-badge-border bg-badge-lime text-badge-lime-foreground disabled:!bg-badge-lime",
				ochre:
					"border-badge-border bg-badge-ochre text-badge-ochre-foreground disabled:!bg-badge-ochre",
				rose: "border-badge-border bg-badge-rose text-badge-rose-foreground disabled:!bg-badge-rose",
			},
			shape: {
				default: "rounded-[4px] h-[24px] px-1.5 uppercase",
				// Same box as `default` but preserves the label's natural casing.
				// Use for badges that display user-facing content (names, emails,
				// free text) where forcing uppercase would distort the value.
				label: "rounded-[4px] h-[24px] px-1.5",
				pill: "rounded-full px-1 min-w-4 h-4",
				dot: "rounded-full px-2.5 gap-1.5",
			},
			// `size` intentionally comes after `shape` so its height wins the
			// tailwind-merge conflict against the shape's height when both are set.
			size: {
				default: "",
				// Fits inside inputs/selects (e.g. min-h-9 triggers) without
				// forcing them to grow. `py-0` reclaims room so the text is not
				// clipped at this reduced height.
				sm: "h-5 py-0",
			},
		},
		defaultVariants: {
			variant: "default",
			shape: "default",
			size: "default",
		},
	},
);

export type BadgeColor = NonNullable<
	VariantProps<typeof badgeVariants>["variant"]
>;

/**
 * The element a badge renders as.
 *
 * `div` is the default, because that is what every existing badge is and what a
 * table cell or a header wants. `span` is for a badge sitting INSIDE prose: a
 * `<div>` is not phrasing content, so a browser parsing one inside a `<p>` closes
 * the paragraph early — the badge silently lands on its own line, and React logs
 * "<div> cannot be a descendant of <p>". Nothing else changes: the base class
 * list already sets `inline-flex`, so both render identically.
 */
export type BadgeElement = "div" | "span";

/** Props for the {@link Badge} component. Combines native element attributes with {@link badgeVariants}. */
export type BadgeProps = React.HTMLAttributes<HTMLElement> &
	VariantProps<typeof badgeVariants> & {
		/** Element to render. Defaults to `"div"`; use `"span"` inline in prose. */
		as?: BadgeElement;
	};

/** Inline status label with colour and shape variants driven by {@link badgeVariants}. */
const Badge = React.forwardRef<HTMLElement, BadgeProps>(
	({ as, className, variant, shape, size, children, ...props }, ref) => {
		const Comp = (as ?? "div") as React.ElementType;

		return (
			<Comp
				ref={ref}
				className={cn(badgeVariants({ variant, shape, size }), className)}
				{...props}
			>
				{shape === "dot" && (
					<span
						className="h-1.5 w-1.5 shrink-0 self-center rounded-full bg-current"
						data-testid="badge-dot"
					/>
				)}
				{children}
			</Comp>
		);
	},
);
Badge.displayName = "Badge";

export { Badge, badgeVariants };
